// v0.7 Public Enemy: one big boss a week that the whole city takes on together.
import { useState } from 'react';
import { api } from '../api.ts';
import { useGame, money, fmt, duration } from '../state.tsx';
import { Loading, useLoad, go, Panel, ItemIcon, PlayerLink } from '../components/ui.tsx';
import { Portrait } from '../art/Portrait.tsx';
import { StatIcon } from '../art/Scenes.tsx';
import { play, sequence } from '../fx/sound.ts';
import { fx, centerOf } from '../fx/fx.tsx';

export function PublicEnemyTab() {
  const { me, act, setFlash, content } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/cityboss'), [], 20_000);
  const [busy, setBusy] = useState(false);
  const [hits, setHits] = useState<{ n: number; damage: number[] } | null>(null);
  if (!data) return <Loading />;
  const b = data.boss;
  const r = data.rules;
  const trophyId = b.trophyId;
  const down = b.status !== 'active';

  const hit = async (times: number) => {
    if (busy) return;
    setBusy(true);
    const res = await act('/api/cityboss/hit', { times });
    setBusy(false);
    if (!res) return;
    const n = Date.now();
    setHits({ n, damage: res.damage });
    // One thud per blow, quick-fire, then the boss reacts.
    const step = Math.max(110, Math.min(220, 1300 / res.damage.length));
    sequence(res.damage.map((_: number, i: number) => [i * step, i % 3 === 2 ? 'punch' : 'bossHit'] as [number, string]));
    const at = centerOf(document.querySelector('.pe-poster .wanted-photo'));
    if (res.beaten) {
      const t = res.damage.length * step;
      sequence([[t + 200, 'roar'], [t + 900, 'horn'], [t + 1500, 'fanfare'], [t + 1700, 'chaching']]);
      setTimeout(() => {
        fx.stamp('Public Enemy down', `${b.name} · +${money(res.reward ?? 0)}`, 'gold');
        fx.coins(30, at);
      }, t + 900);
      setFlash({ kind: 'good', title: `${b.name} is down!`, text: <>{b.defeat} You landed the final blow. Everyone who hit them gets paid by their share of the damage{res.reward ? <>: your cut is <span className="money">{money(res.reward)}</span></> : null}.</> });
    } else if (res.damage.length < times) {
      setFlash({ kind: 'bad', text: <>You landed {res.damage.length} of {times} hits before you got too hurt to go on. Heal up at the <a onClick={() => go('hospital')}>hospital</a>.</> });
    }
    reload();
  };
  const pct = (b.hp / Math.max(1, b.maxHp)) * 100;
  const canHit = !down && me.level >= r.minLevel && me.stamina >= r.staminaPerHit && me.health >= r.minHealth + r.counterDamage;
  return (
    <div>
      <p className="muted">
        Each week a Public Enemy comes to town and the whole city takes them on together. Every hit costs {r.staminaPerHit} stamina and they hit back for {r.counterDamage} health.
        When they go down, <b>everyone who hit them gets paid</b> by their share of the damage, and the top hitter keeps a trophy. If they’re still standing on Monday, they get away.
      </p>
      <div className="row wanted-row">
        <div className={`pe-poster ${down ? 'down' : ''}`}>
          <div className="wanted-head">Wanted</div>
          <div className="wanted-sub">Dead or alive · by order of the city</div>
          <div className={`wanted-photo ${hits ? 'hit' : ''}`} key={hits?.n ?? 0}>
            <Portrait seed={b.portrait} size={150} />
            {hits?.damage.map((d, i) => <span key={i} className="cb-dmg" style={{ animationDelay: `${i * Math.max(110, Math.min(220, 1300 / hits.damage.length))}ms`, left: `${18 + ((i * 37) % 64)}%` }}>-{fmt(d)}</span>)}
            {down && <span className="wanted-stamp">{b.status === 'beaten' ? 'Captured' : 'Escaped'}</span>}
          </div>
          <div className="wanted-name">{b.name}</div>
          <div className="wanted-title">{b.title}</div>
          <div className="cb-hp"><div style={{ width: `${pct}%` }} /></div>
          <div className="cb-hp-label">{fmt(b.hp)} / {fmt(b.maxHp)} health</div>
          <div className="small center" style={{ marginTop: 4 }}>{down ? (b.status === 'beaten' ? 'Taken down. A new one arrives on Monday.' : 'Got away.') : <>On the loose for another {duration(Math.max(0, b.endsAt - Date.now()))}</>}</div>
        </div>
        <div className="grow">
          <Panel title="Take your shot">
            <div className="small" style={{ marginBottom: 6 }}>{b.arrival}</div>
            <div className="row" style={{ gap: 14, flexWrap: 'wrap', marginBottom: 8 }}>
              <span><StatIcon kind="attack" /> Your hit: ~<b>{fmt(data.myStrength)}</b></span>
              <span><StatIcon kind="stamina" /> Stamina <b>{me.stamina}</b></span>
              <span><StatIcon kind="health" /> Health <b>{me.health}</b></span>
            </div>
            {me.level < r.minLevel ? <div className="bad">You can take on the Public Enemy at level {r.minLevel}.</div> : (
              <div className="boss-moves" style={{ justifyContent: 'flex-start' }}>
                {[1, 5, 10].map((t) => (
                  <button key={t} className={`btn big ${t === 1 ? '' : 'red'} ${canHit && me.stamina >= t * r.staminaPerHit ? '' : 'disabled'}`} disabled={busy} onClick={() => hit(t)}>
                    Hit {t === 1 ? 'once' : `${t}×`} <span className="small">({t * r.staminaPerHit} stamina)</span>
                  </button>
                ))}
              </div>
            )}
            {!down && me.health < r.minHealth + r.counterDamage && <div className="small bad">You’re too hurt to fight. <a onClick={() => go('hospital')}>Heal up</a>.</div>}
            <div className="cb-me">
              {data.me ? <>You’ve done <b>{fmt(data.me.damage)}</b> damage in {fmt(data.me.hits)} hits: <b>#{data.me.rank}</b>, {Math.round(data.me.share * 100)}% of the total.
                {down && data.me.reward ? <> You were paid <span className="money">{money(data.me.reward)}</span>.</> : !down && <> If they went down now you’d get about <span className="money">{money(data.estimate)}</span>.</>}</>
                : <>You haven’t hit them yet. Even one hit gets you a share of the reward.</>}
            </div>
            <div className="cb-trophy">
              <ItemIcon itemId={trophyId} size={40} />
              <div className="small">Top hitter keeps <b>{b.trophy}</b>, a one-of-a-kind trophy.</div>
            </div>
          </Panel>
          <Panel title={`Damage board · ${data.participants} taking part`}>
            {data.board.length === 0 ? <div className="muted">Nobody has landed a hit yet. Be the first.</div> : (
              <table className="list">
                <tbody>
                  {data.board.map((x: any) => (
                    <tr key={x.id} className={x.id === me.id ? 'me-row' : ''}>
                      <td style={{ width: 26 }}><b>{x.rank}</b></td>
                      <td style={{ width: 30 }}><Portrait seed={x.portrait} playerId={x.id} size={24} /></td>
                      <td><PlayerLink id={x.id} name={x.name} />{x.isAi && <span className="small dim"> (computer)</span>}</td>
                      <td className="right">{fmt(x.damage)} <span className="small dim">{Math.round(x.share * 100)}%</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
          {data.lastWeek && <div className="small dim">Last week: {data.lastWeek.name} {data.lastWeek.status === 'beaten' ? 'was taken down.' : `got away with ${Math.round((data.lastWeek.hpLeft / data.lastWeek.maxHp) * 100)}% health left.`}</div>}
        </div>
      </div>
      <div className="small dim">Computer players chip in a little, but most of the work is up to you{content.gameName ? ` and the rest of ${content.gameName}` : ''}.</div>
    </div>
  );
}
